import { useMemo, useState } from 'react';
import { BarChart3 } from 'lucide-react';
import { financialYearOf, financialYearPeriod, currentMonth } from '@/lib/dates';
import { usePermissions } from '@/hooks/usePermissions';
import { useFiscalYearStartMonth } from '@/hooks/useOrgSettings';
import { Button } from '@/features/common/ui';
import { BreakdownCard } from '@/features/reports/BreakdownCard';
import { MonthsChart } from '@/features/reports/MonthsChart';

/**
 * The picture above the Expenses list: this financial year's spend month by
 * month, and where it went by category, vendor or project - the same cards
 * the Dashboard draws, here because this is the page a person is on when
 * they ask "where is it going". Off by default and remembered per browser:
 * the list is what most visits are for, and a chart the person did not ask
 * for pushes the first expense below the fold on a phone.
 */
export function SpendingPanel() {
  const perms = usePermissions();
  const fyStartMonth = useFiscalYearStartMonth();
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem('fint:expenses:spending') === 'open';
    } catch {
      return false;
    }
  });
  const fy = useMemo(() => financialYearPeriod(financialYearOf(currentMonth().from, fyStartMonth).startYear, fyStartMonth), [fyStartMonth]);
  // The API refuses a spend report to anyone who only sees their own expenses.
  if (!perms.seesAll('report')) return null;

  const toggle = () => {
    setOpen((current) => {
      const next = !current;
      try {
        localStorage.setItem('fint:expenses:spending', next ? 'open' : 'closed');
      } catch {
        // Not remembered; nothing else changes.
      }
      return next;
    });
  };

  return (
    <div className="space-y-4" data-testid="spending-panel">
      <Button type="button" variant={open ? 'secondary' : 'outline'} size="sm" onClick={toggle} aria-expanded={open} data-testid="spending-toggle">
        <BarChart3 className="size-4" aria-hidden />
        {open ? 'Hide the picture' : `Where it went · ${fy.label}`}
      </Button>
      {open ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <MonthsChart side="expense" period={fy} basis="accrual" title={`Month by month · ${fy.label}`} className="lg:col-span-3" />
          <BreakdownCard side="expense" period={fy} basis="accrual" title="Where it went" className="lg:col-span-2" />
        </div>
      ) : null}
    </div>
  );
}
